import { useMemo } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, type FieldPath, type Resolver, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { MarkdownEditor } from "@/components/MarkdownEditor";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { getTargetLabel, TARGET_VALUES } from "../../adapter";
import { type TermsAndConditionsModalOptions } from "../../types";

interface FormProps {
  initialValues?: Partial<TermsOfUseFormValues>;
  modalOptions: TermsAndConditionsModalOptions;
  onSubmit: (values: TermsOfUseFormValues) => void;
}

export const TERMS_OF_USE_FORM_ID = "terms-of-use-form";

export const createTermsOfUseSchema = (requiredMessage: string) =>
  z.object({
    long_text: z.preprocess(
      value => value ?? "",
      z.string().trim().min(1, { message: requiredMessage })
    ),
    short_text: z.preprocess(
      value => value ?? "",
      z.string().trim().min(1, { message: requiredMessage })
    ),
    target: z.enum(TARGET_VALUES, { message: requiredMessage }),
  });

export type TermsOfUseFormValues = z.infer<ReturnType<typeof createTermsOfUseSchema>>;

const DEFAULT_FORM_VALUES = {
  long_text: "",
  short_text: "",
  target: undefined,
};

const toErrorMessage = (error: unknown): string => {
  if (typeof error === "string") {
    return error;
  }

  if (error && typeof error === "object" && "message" in error) {
    const message = (error as { message?: unknown }).message;
    return typeof message === "string" ? message : "";
  }

  return "";
};

export const normalizeInitialValues = (initialValues?: Partial<TermsOfUseFormValues>) => ({
  ...DEFAULT_FORM_VALUES,
  long_text: initialValues?.long_text ?? DEFAULT_FORM_VALUES.long_text,
  short_text: initialValues?.short_text ?? DEFAULT_FORM_VALUES.short_text,
  target: initialValues?.target,
});

export const Form = ({ initialValues, modalOptions, onSubmit }: FormProps) => {
  const { t } = useTranslation();
  // Terms are versioned: editing publishes a new record for the same audience,
  // so the target must not change once it has been chosen.
  const isTargetLocked = modalOptions.type === "edit";
  const schema = useMemo(
    () => createTermsOfUseSchema(t("traderDetails.form.mandatoryFieldBlankError")),
    [t]
  );
  const defaultValues = useMemo(() => normalizeInitialValues(initialValues), [initialValues]);
  const targetOptions = useMemo(
    () => TARGET_VALUES.map(value => ({ label: getTargetLabel(value, t), value })),
    [t]
  );

  // Seeded once at mount: the modal remounts this form per record, and a later
  // reset() does not reach the target Select.
  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<TermsOfUseFormValues>({
    defaultValues,
    mode: "onSubmit",
    reValidateMode: "onChange",
    resolver: zodResolver(schema) as Resolver<TermsOfUseFormValues>,
  });

  const getFieldError = (fieldName: FieldPath<TermsOfUseFormValues>) =>
    toErrorMessage(errors[fieldName]?.message);

  return (
    <form
      id={TERMS_OF_USE_FORM_ID}
      className="flex flex-col gap-5"
      noValidate
      onSubmit={handleSubmit(onSubmit)}
    >
      <Controller
        control={control}
        name="target"
        render={({ field }) => {
          const error = getFieldError("target");

          return (
            <Field data-invalid={Boolean(error)}>
              <FieldLabel htmlFor="input-target">
                {t("termsAndConditionsText.list.target")}
                <span aria-hidden="true" className="text-destructive">
                  *
                </span>
              </FieldLabel>
              <Select
                value={field.value ?? ""}
                disabled={isTargetLocked}
                onValueChange={field.onChange}
              >
                <SelectTrigger
                  id="input-target"
                  className="w-full"
                  aria-invalid={Boolean(error)}
                  aria-describedby={error ? "input-target-error" : undefined}
                >
                  <SelectValue placeholder={t("common.select")} />
                </SelectTrigger>
                <SelectContent>
                  {targetOptions.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FieldError id="input-target-error">{error}</FieldError>
            </Field>
          );
        }}
      />

      <Controller
        control={control}
        name="short_text"
        render={({ field }) => {
          const error = getFieldError("short_text");

          return (
            <Field data-invalid={Boolean(error)}>
              <FieldLabel htmlFor="input-short-text">
                {t("termsAndConditionsText.list.shortText")}
                <span aria-hidden="true" className="text-destructive">
                  *
                </span>
              </FieldLabel>
              <MarkdownEditor
                field={field}
                textareaProps={{
                  id: "input-short-text",
                  "aria-invalid": Boolean(error),
                  "aria-describedby": error ? "input-short-text-error" : undefined,
                }}
              />
              <FieldError id="input-short-text-error">{error}</FieldError>
            </Field>
          );
        }}
      />

      <Controller
        control={control}
        name="long_text"
        render={({ field }) => {
          const error = getFieldError("long_text");

          return (
            <Field data-invalid={Boolean(error)}>
              <FieldLabel htmlFor="input-long-text">
                {t("termsAndConditionsText.list.longText")}
                <span aria-hidden="true" className="text-destructive">
                  *
                </span>
              </FieldLabel>
              <MarkdownEditor
                field={field}
                textareaProps={{
                  id: "input-long-text",
                  "aria-invalid": Boolean(error),
                  "aria-describedby": error ? "input-long-text-error" : undefined,
                }}
              />
              <FieldError id="input-long-text-error">{error}</FieldError>
            </Field>
          );
        }}
      />
    </form>
  );
};
